import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Building2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export const Route = createFileRoute("/bolag-login")({
  head: () => ({
    meta: [
      { title: "Bolagsinloggning — Inkubera" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BolagLogin,
});

function BolagLogin() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"magic" | "password">("password");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/bolag-portal" });
  }, [loading, user, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    if (mode === "password") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      setBusy(false);
      if (error) { setError("Fel e-post eller lösenord."); return; }
      navigate({ to: "/bolag-portal" });
      return;
    }
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/bolag-portal`,
        shouldCreateUser: false,
      },
    });
    setBusy(false);
    if (error) {
      setError("Kunde inte skicka länk. Kontrollera adressen eller kontakta Inkubera.");
      return;
    }
    setSent(true);
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-8 py-6">
        <Link to="/" className="inline-flex items-center gap-2.5">
          <div className="size-9 rounded-xl bg-foreground flex items-center justify-center text-background">
            <Sparkles className="size-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-lg leading-none">Inkubera</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
              Portfolio Intelligence
            </div>
          </div>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-border bg-card p-8">
            <div className="size-10 rounded-xl bg-secondary flex items-center justify-center mb-5">
              <Building2 className="size-4" />
            </div>
            <h1 className="font-display text-2xl">Bolagsinloggning</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Endast registrerade portföljbolag har åtkomst.
            </p>

            <div className="mt-4 inline-flex rounded-lg bg-secondary p-1 text-xs">
              <button type="button" onClick={() => setMode("password")} className={`px-3 py-1.5 rounded-md ${mode === "password" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>Lösenord</button>
              <button type="button" onClick={() => setMode("magic")} className={`px-3 py-1.5 rounded-md ${mode === "magic" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>Magisk länk</button>
            </div>

            {sent ? (
              <div className="mt-6 text-sm bg-secondary rounded-lg px-4 py-3">
                Länk skickad till <span className="font-medium">{email}</span>. Öppna mejlet
                och klicka för att logga in.
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">E-post</label>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
                  />
                </div>

                {mode === "password" && (
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Lösenord</label>
                    <input
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
                    />
                  </div>
                )}

                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="w-full h-10 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? (mode === "password" ? "Loggar in…" : "Skickar…") : (mode === "password" ? "Logga in" : "Skicka inloggningslänk")}
                </button>
                {mode === "password" && (
                  <div className="text-center">
                    <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground underline">
                      Glömt lösenord?
                    </Link>
                  </div>
                )}
              </form>
            )}

            <p className="mt-6 text-xs text-muted-foreground text-center">
              Inkubera-administratör måste först ha kopplat din e-post till bolaget.
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
              ← Tillbaka
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
